import { escape, unescape } from 'html-escaper';
import striptags from 'striptags';
import { exportPageHTML } from '@/constants';
// Import also sets nunjucks autoescape:false, which getContextKeySubstitutedTitle needs.
import { removeTooltipSpanTag } from '@/lib/htmlParser';
import { getContextKeySubstitutedTitle } from '@/lib/util';

const POPUP_BLOCKED_MESSAGE =
  'Unable to open the printable view. Please allow pop-ups for this site and try again.';

const DEFAULT_TITLE = 'Print View';

// previousValues wraps placeholders in a tooltip span. Titles are plain text, so the
// tooltip must go first or it survives as "This value can't be edited.".
function withoutTooltips(contextValues) {
  return Object.fromEntries(
    Object.entries(contextValues).map(([key, value]) => [
      key,
      typeof value === 'string' ? removeTooltipSpanTag(value) : value
    ])
  );
}

// CEModuleRenderer drops the markdown's first line because Page.vue renders it as a
// separate header, so the print view rebuilds it here.
function buildHistoricalTitle(markdown, contextValues = {}) {
  if (!markdown) return '';

  const index = markdown.indexOf('\n');
  const rawTitle = index === -1 ? markdown : markdown.substring(0, index);
  const title = unescape(striptags(rawTitle)).replace(/#/g, '').trim();

  return getContextKeySubstitutedTitle(title, withoutTooltips(contextValues));
}

// Second argument of window.open is the window NAME — it must vary per version or a
// second print silently reuses the first window.
function buildWindowName(printTitle, versionDate) {
  return `historical-print-${printTitle}-${versionDate || ''}`.replace(/\W+/g, '-');
}

// Callers must inline the images first (see HistoricalPrintRenderer.printVersion).
async function openPrintWindow({ html, title, logoUri, versionDate }) {
  const printTitle = title || DEFAULT_TITLE;
  const printWindow = window.open(
    '',
    buildWindowName(printTitle, versionDate),
    'toolbar=0,titlebar=1,scrollbars=1,status=0'
  );

  if (!printWindow) {
    throw new Error(POPUP_BLOCKED_MESSAGE);
  }

  // Both dates are the version timestamp so header and footer describe that version.
  printWindow.document.write(
    exportPageHTML(
      versionDate,
      versionDate,
      escape(printTitle),
      logoUri,
      `<h1>${escape(printTitle)}</h1>${html}`
    )
  );

  printWindow.document.close();
  printWindow.focus();

  return printWindow;
}

export { buildHistoricalTitle, openPrintWindow, POPUP_BLOCKED_MESSAGE };
